import type { BRAND_COLORS } from "@/components/landing/StoreCustomizer/config";
import type { FontId, SectionId, TemplateId } from "@/components/landing/StoreCustomizer/types";
import { faUpload } from "@fortawesome/pro-solid-svg-icons";
import { useRef } from "react";
import ColorPicker from "@/components/landing/StoreCustomizer/ColorPicker";
import FontPicker from "@/components/landing/StoreCustomizer/FontPicker";
import SectionVisibilityList from "@/components/landing/StoreCustomizer/SectionVisibilityList";
import ThemePicker from "@/components/landing/StoreCustomizer/ThemePicker";
import { Icon } from "@/components/ui/Icon";

interface ControlPanelProps {
	templateLabel: string;
	templates: Record<TemplateId, string>;
	templateId: TemplateId;
	onTemplateChange: (id: TemplateId) => void;
	logoLabel: string;
	uploadLabel: string;
	removeLogoLabel: string;
	logoUrl: string | null;
	onLogoChange: (url: string | null) => void;
	colorLabel: string;
	colorId: (typeof BRAND_COLORS)[number]["id"];
	onColorChange: (id: (typeof BRAND_COLORS)[number]["id"]) => void;
	fontLabel: string;
	fonts: Record<FontId, string>;
	fontId: FontId;
	onFontChange: (id: FontId) => void;
	sectionsLabel: string;
	sections: Record<SectionId, string>;
	visible: Record<SectionId, boolean>;
	accent: string;
	onToggleSection: (id: SectionId) => void;
}

function ControlPanel(prop: ControlPanelProps) {
	const {
		templateLabel,
		templates,
		templateId,
		onTemplateChange,
		logoLabel,
		uploadLabel,
		removeLogoLabel,
		logoUrl,
		onLogoChange,
		colorLabel,
		colorId,
		onColorChange,
		fontLabel,
		fonts,
		fontId,
		onFontChange,
		sectionsLabel,
		sections,
		visible,
		accent,
		onToggleSection,
	} = prop;
	const fileInputRef = useRef<HTMLInputElement>(null);
	return (
		<div className="flex flex-col gap-6 border-b border-border p-5 sm:border-b-0 sm:border-e">
			<ThemePicker
				label={templateLabel}
				templates={templates}
				templateId={templateId}
				accent={accent}
				onChange={onTemplateChange}
			/>

			<div className="flex items-center justify-between gap-3">
				<p className="text-xs font-semibold text-muted-foreground">{logoLabel}</p>
				<div className="flex items-center gap-2">
					{logoUrl && (
						<button
							type="button"
							onClick={() => onLogoChange(null)}
							aria-label={removeLogoLabel}
							className="relative h-6 w-6 shrink-0 overflow-hidden rounded-full border border-border"
						>
							{/* oxlint-disable-next-line nextjs/no-img-element -- ephemeral blob: preview, not an optimizable asset */}
							<img src={logoUrl} alt="" className="h-full w-full object-cover" />
						</button>
					)}
					<button
						type="button"
						onClick={() => fileInputRef.current?.click()}
						className="btn btn-outline gap-1.5 px-2.5 py-1 text-xs"
					>
						<Icon icon={faUpload} size={13} />
						{uploadLabel}
					</button>
					<input
						ref={fileInputRef}
						type="file"
						accept="image/*"
						className="hidden"
						onChange={(e) => {
							const file = e.target.files?.[0];
							if (file) onLogoChange(URL.createObjectURL(file));
							e.target.value = "";
						}}
					/>
				</div>
			</div>

			<ColorPicker label={colorLabel} colorId={colorId} onChange={onColorChange} />

			<FontPicker label={fontLabel} fonts={fonts} fontId={fontId} onChange={onFontChange} />

			<SectionVisibilityList
				label={sectionsLabel}
				sections={sections}
				visible={visible}
				accent={accent}
				onToggle={onToggleSection}
			/>
		</div>
	);
}

export default ControlPanel;
